import { useMemo, useState } from 'react';

import {
	Alert,
	Badge,
	Box,
	Button,
	Group,
	LoadingOverlay,
	ScrollArea,
	Table,
	Text,
} from '@mantine/core';

import { useQuery } from '@tanstack/react-query';

import { scudMappingApi } from '@/shared/api/scudMapping';
import { IScudMappingReason } from '@/shared/types/scudMapping.types';

import { IconPlus } from '@tabler/icons-react';

import { ScudReasonForm } from './ScudReasonForm';
import { CellLines, EditIconButton, ScudTable, ScudTh, useTableSort } from './ScudTable';
import { SortableTh } from './SortableTh';
import { SCUD_REASONS_ALL_KEY } from './queryKeys';
import { requestErrorText } from './utils';

type TReasonSort = 'sort_order' | 'name' | 'comment_required' | 'is_active';

const sortValue = (reason: IScudMappingReason, field: TReasonSort) => {
	const value = reason[field];

	return typeof value === 'boolean' ? Number(value) : value;
};

/** Вкладка «Причины» — только администратор Бронирования */
export const ScudReasons = () => {
	const [editing, setEditing] = useState<IScudMappingReason | null>(null);
	const [isFormOpened, setIsFormOpened] = useState(false);

	const {
		data: reasons = [],
		isLoading,
		isError,
		error,
	} = useQuery({
		queryKey: SCUD_REASONS_ALL_KEY,
		queryFn: () => scudMappingApi.getReasons(false),
		staleTime: 300_000,
	});

	const { sort, desc, onSort } = useTableSort<TReasonSort>('sort_order');
	const thProps = { sort, desc, onSort };

	// Список небольшой и приходит целиком, поэтому сортируем на клиенте
	const sorted = useMemo(
		() =>
			[...reasons].sort((a, b) => {
				const left = sortValue(a, sort);
				const right = sortValue(b, sort);
				const result =
					typeof left === 'string' && typeof right === 'string'
						? left.localeCompare(right, 'ru')
						: Number(left) - Number(right);

				return desc ? -result : result;
			}),
		[reasons, sort, desc],
	);

	const nextSortOrder = reasons.reduce((max, item) => Math.max(max, item.sort_order), 0) + 10;

	const openForm = (reason: IScudMappingReason | null) => {
		setEditing(reason);
		setIsFormOpened(true);
	};

	return (
		<Box
			pos="relative"
			style={{ display: 'flex', flexDirection: 'column', gap: 12, overflow: 'hidden', flex: 1 }}
		>
			<LoadingOverlay visible={isLoading} zIndex={100} overlayProps={{ radius: 2, blur: 2 }} />
			<Group justify="space-between">
				<Text size="sm" c="dimmed">
					Причины для статуса «Сопоставление не требуется» и для исключений
				</Text>
				<Button leftSection={<IconPlus size={16} />} onClick={() => openForm(null)}>
					Добавить
				</Button>
			</Group>

			{isError && (
				<Alert color="red" variant="light">
					{requestErrorText(error, 'Не удалось загрузить причины')}
				</Alert>
			)}

			<ScrollArea style={{ flex: 1 }} scrollbarSize={12} offsetScrollbars>
				<ScudTable>
					<Table.Thead>
						<Table.Tr>
							<SortableTh {...thProps} field="sort_order" width={90}>
								Порядок
							</SortableTh>
							<SortableTh {...thProps} field="name">
								Название
							</SortableTh>
							<SortableTh {...thProps} field="comment_required">
								Комментарий
							</SortableTh>
							<SortableTh {...thProps} field="is_active">
								Состояние
							</SortableTh>
							<ScudTh>Действия</ScudTh>
						</Table.Tr>
					</Table.Thead>
					<Table.Tbody>
						{sorted.map((reason) => (
							<Table.Tr key={reason.id}>
								<Table.Td>
									<Text size="sm" c="dimmed">
										{reason.sort_order}
									</Text>
								</Table.Td>
								<Table.Td>
									<CellLines main={reason.name} />
								</Table.Td>
								<Table.Td>
									<Text size="sm">{reason.comment_required ? 'Обязателен' : 'По желанию'}</Text>
								</Table.Td>
								<Table.Td>
									<Badge
										color={reason.is_active ? 'green' : 'gray'}
										variant="light"
										radius="sm"
										tt="none"
										miw="max-content"
									>
										{reason.is_active ? 'Используется' : 'Не используется'}
									</Badge>
								</Table.Td>
								<Table.Td style={{ width: '1%', whiteSpace: 'nowrap' }}>
									<EditIconButton onClick={() => openForm(reason)} />
								</Table.Td>
							</Table.Tr>
						))}
					</Table.Tbody>
				</ScudTable>
				{!isLoading && !isError && !reasons.length && (
					<Text c="dimmed" ta="center" py="xl">
						Причин нет
					</Text>
				)}
			</ScrollArea>

			<ScudReasonForm
				opened={isFormOpened}
				reason={editing}
				nextSortOrder={nextSortOrder}
				onClose={() => setIsFormOpened(false)}
			/>
		</Box>
	);
};
